<template>
  <span class="app-badge" :class="rootClasses">
    <slot />
  </span>
</template>

<script>
/**
 * Compact colored pill — used for status, type, role, and other categorical
 * labels. `variant` chooses palette; `size` chooses density; `outlined`
 * switches from filled (soft bg) to bordered-flat look.
 *
 * Slots:
 *   default — label text or any inline content.
 */
export default {
  name: 'AppBadge',

  props: {
    variant: {
      type: String,
      default: 'gray',
      validator: (v) => [
        'blue', 'green', 'amber', 'gray',
        'teal', 'red', 'purple',
      ].includes(v),
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md'].includes(v),
    },
    outlined: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    rootClasses() {
      return [
        `app-badge--${this.variant}`,
        `app-badge--${this.size}`,
        { 'app-badge--outlined': this.outlined },
      ]
    },
  },
}
</script>

<style lang="scss" scoped>
// Self-contained tokens — keep the component portable.
$ab-blue:        #1f73c1;
$ab-blue-soft:   rgba(31, 115, 193, 0.08);
$ab-blue-edge:   rgba(31, 115, 193, 0.20);
$ab-green:       #2da44e;
$ab-green-soft:  rgba(45, 164, 78, 0.12);
$ab-green-edge:  rgba(45, 164, 78, 0.22);
$ab-amber:       #c4801c;
$ab-amber-soft:  rgba(196, 128, 28, 0.12);
$ab-amber-edge:  rgba(196, 128, 28, 0.22);
$ab-red:         #cf222e;
$ab-red-soft:    rgba(207, 34, 46, 0.10);
$ab-red-edge:    rgba(207, 34, 46, 0.22);
$ab-teal:        #0ea596;
$ab-teal-soft:   rgba(14, 165, 150, 0.12);
$ab-teal-edge:   rgba(14, 165, 150, 0.22);
$ab-purple:      #6d46c1;
$ab-purple-soft: rgba(109, 70, 193, 0.10);
$ab-purple-edge: rgba(109, 70, 193, 0.22);
$ab-gray:        #6c7a89;
$ab-gray-soft:   #eef1f4;
$ab-gray-edge:   rgba(0, 0, 0, 0.08);

.app-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-weight: 500;
  white-space: nowrap;
  line-height: 1.4;
  border: 0.5px solid transparent;

  // ── Sizes ──
  &--sm { font-size: 10px; padding: 1px 7px; }
  &--md { font-size: 11px; padding: 2px 9px; }

  // ── Filled (default) ──
  &--blue   { background: $ab-blue-soft;   color: $ab-blue;   }
  &--green  { background: $ab-green-soft;  color: $ab-green;  }
  &--amber  { background: $ab-amber-soft;  color: $ab-amber;  }
  &--red    { background: $ab-red-soft;    color: $ab-red;    }
  &--teal   { background: $ab-teal-soft;   color: $ab-teal;   }
  &--purple { background: $ab-purple-soft; color: $ab-purple; }
  &--gray   { background: $ab-gray-soft;   color: $ab-gray;   }

  // ── Outlined (border-flat) ──
  &--outlined {
    background: transparent;

    &.app-badge--blue   { color: $ab-blue;   border-color: $ab-blue-edge;   }
    &.app-badge--green  { color: $ab-green;  border-color: $ab-green-edge;  }
    &.app-badge--amber  { color: $ab-amber;  border-color: $ab-amber-edge;  }
    &.app-badge--red    { color: $ab-red;    border-color: $ab-red-edge;    }
    &.app-badge--teal   { color: $ab-teal;   border-color: $ab-teal-edge;   }
    &.app-badge--purple { color: $ab-purple; border-color: $ab-purple-edge; }
    &.app-badge--gray   { color: $ab-gray;   border-color: $ab-gray-edge;   }
  }
}
</style>
